/**
 * 대표 전용 "오늘 할 일" 표시 (스크린챗 #581).
 *
 * 폴링·포커스 재조회는 하지 않는다. 앱에서 처음 마운트될 때 한 번 읽고, 완료를 누른 직후에만
 * 다시 읽는다. 등록·수정·삭제 화면은 mutation 응답을 같은 Query 캐시에 넣어 표시와 즉시 맞춘다.
 *
 * 2026-08-28: 상단 띠 **아래에 가로로 깔리던 노란 줄**을 띠 **안의 노란 칩**으로 옮겼다.
 * 늘 떠 있는 알림이 세로 한 줄(약 36px)을 통째로 먹는 게 비쌌기 때문이다. 눈에 띄어야 하는
 * 알림이므로 **호박색(amber)은 그대로 두고**, 목록은 칩 아래로 떠오르는 패널이 된다.
 * 할 일이 없으면 아무것도 그리지 않는 동작은 종전과 같다 — 평소엔 폭을 한 픽셀도 안 먹는다.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../common/Toast";
import {
  completeRecurringTask,
  fetchRecurringTasks,
  recurringTasksQk,
} from "../../api/recurringTasks";

export default function RecurringTaskBanner({ className = "" }: { className?: string }) {
  const { isStoreManager } = useAuth();
  const enabled = isStoreManager();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const tasksQ = useQuery({
    queryKey: recurringTasksQk,
    queryFn: fetchRecurringTasks,
    enabled,
    staleTime: Infinity,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const completeMut = useMutation({
    mutationFn: completeRecurringTask,
    onSuccess: async () => {
      // 완료 직후만 서버에서 한 번 다시 읽는다. 다음 주기 계산도 서버의 같은 KST 기준으로 맞춘다.
      await queryClient.refetchQueries({ queryKey: recurringTasksQk, exact: true });
      toast.success("할 일을 완료했습니다");
    },
    onError: (error) => toast.error((error as Error).message),
  });

  // 바깥 클릭·Esc 로 닫는다. 목록 안에서 [완료]를 누르는 동안에는 닫히면 안 되므로
  // 패널 안쪽 클릭은 그대로 통과시킨다.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!enabled || !tasksQ.data || tasksQ.data.dueTasks.length === 0) return null;
  const { dueTasks, todayCount, overdueCount } = tasksQ.data;

  return (
    <div ref={rootRef} className={`relative shrink-0 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title="대표에게 배정된 정기 할 일 — 누르면 목록이 열립니다"
        className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-amber-300 bg-amber-50 px-2.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200 dark:hover:bg-amber-500/20"
      >
        <span aria-hidden>✓</span>
        {/* 넓은 화면에서만 '건'·'일' 까지 적는다 — 좁은 화면에선 숫자만으로도 읽힌다. */}
        <span>
          오늘 {todayCount}
          <span className="hidden 2xl:inline">건</span>
          {overdueCount > 0 && (
            <>
              {" · 밀린 "}
              <span className="hidden 2xl:inline">일 </span>
              {overdueCount}
              <span className="hidden 2xl:inline">건</span>
            </>
          )}
        </span>
        <span className="text-[10px]" aria-hidden>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="오늘 할 일"
          className="absolute left-0 top-full z-99999 mt-1 max-h-72 w-[26rem] max-w-[80vw] overflow-auto rounded-lg border border-amber-200 bg-amber-50 p-2 shadow-theme-lg dark:border-amber-500/30 dark:bg-gray-900"
        >
          <ul className="space-y-2">
            {dueTasks.map((task) => (
              <li
                key={task.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 shadow-sm dark:bg-gray-800"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">
                    {task.title}
                    {task.isOverdue && (
                      <span className="ml-2 text-xs font-semibold text-error-600 dark:text-error-400">
                        · {task.overdueDays}일째 밀림
                      </span>
                    )}
                  </p>
                  {!task.isOverdue && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">오늘 챙길 일</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => completeMut.mutate(task.id)}
                  disabled={completeMut.isPending}
                  className="h-8 shrink-0 rounded-lg bg-brand-500 px-3 text-xs font-semibold text-white transition hover:bg-brand-600 disabled:opacity-50"
                >
                  완료
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
